import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { APP_ENVIRONMENT } from '@core/config/app-environment';
import { EventTrackerResult, EventTrackerService } from '@core/event-tracker';
import { FirebaseService } from '@core/firebase/firebase.service';
import { SessionAccount } from '@data-access/models/session.models';
import { SessionApi } from '@data-access/session-api';
import { Modules, productCode, StorageKeys } from '@shared/constants/commons';
import { TenantKey, tenantEquivalents } from '@shared/utils/tenants';
import {
  getIdToken,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';
import {
  catchError,
  defaultIfEmpty,
  defer,
  EMPTY,
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AccountSummary, AuthResponse, LoginRequest } from './auth.models';
import { ModuleSelectionStore } from './module-selection.store';

/**
 * Picker authentication. Port of legacy AuthService:
 * Firebase Auth (email/password or Microsoft) -> cc-auth cookie session (`API_AUTH_LOGIN`), same endpoints and
 * payloads. The account (role, permissions, organizations) is kept in memory only: after login it is the cc-auth
 * response and after a reload it is loaded again from cc-rt-core `GET /api/v1/session/me`. Unlike legacy, it is
 * never stored in localStorage (`auth_user`), so editing the browser storage does not grant access. Only
 * `auth_module` and `auth_session_issued_at` are stored.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly firebase = inject(FirebaseService);
  private readonly http = inject(HttpClient);
  private readonly env = inject(APP_ENVIRONMENT);
  private readonly moduleStore = inject(ModuleSelectionStore);
  private readonly tracker = inject(EventTrackerService);
  private readonly sessionApi = inject(SessionApi);

  private readonly baseUrl = `${this.env.API_AUTH_LOGIN}/api/v1/auth`;

  private readonly _session = signal<AuthResponse | null>(null);
  /** Current session (null when logged out). */
  readonly session = this._session.asReadonly();
  readonly currentUser = computed<AccountSummary | null>(() => this._session()?.account ?? null);

  private refreshInProgress$: Observable<void> | null = null;
  private sessionLoad$: Observable<AccountSummary | null> | null = null;

  loginWithEmailAndPassword(request: LoginRequest): Observable<AuthResponse> {
    return from(signInWithEmailAndPassword(this.firebase.auth, request.email, request.password)).pipe(
      catchError((error) => {
        console.error('Firebase login error:', error);
        return throwError(() => error);
      }),
      switchMap((credential: UserCredential) => this.exchangeFirebaseToken(credential, { ...request })),
    );
  }

  loginWithMicrosoftApi(): Observable<AuthResponse> {
    const provider = new OAuthProvider('microsoft.com');
    if (this.env.firebaseProvider) {
      const { prompt, tenant } = this.env.firebaseProvider;
      provider.setCustomParameters({ prompt, tenant });
    }

    return from(signInWithPopup(this.firebase.auth, provider)).pipe(
      catchError((error) => {
        console.error('Firebase Microsoft login error:', error);
        return throwError(() => error);
      }),
      switchMap((credential: UserCredential) => this.exchangeFirebaseToken(credential, {})),
    );
  }

  /** Firebase ID token -> `POST /session/login` (cookie session). */
  private exchangeFirebaseToken(credential: UserCredential, extra: Partial<LoginRequest>): Observable<AuthResponse> {
    return from(credential.user.getIdToken()).pipe(
      catchError((error) => {
        console.error('Error getting token:', error);
        return throwError(() => error);
      }),
      switchMap((firebaseToken: string) =>
        this.http
          .post<AuthResponse>(`${this.baseUrl}/session/login`, { ...extra, firebaseToken, productCode })
          .pipe(
            tap((resp) => this.handleAuthSuccess(resp)),
            catchError((error) => {
              console.error('API login error:', error);
              return throwError(() => error);
            }),
          ),
      ),
    );
  }

  private handleAuthSuccess(resp: AuthResponse): void {
    const session: AuthResponse = { account: this.withTenantAndStore(resp.account) };

    // Legacy stores the selected module as-is (a null module is stored as "null").
    localStorage.setItem(StorageKeys.AUTH_MODULE, String(this.moduleStore.getCurrentModule()));
    localStorage.setItem(StorageKeys.AUTH_SESSION_ISSUED, Date.now().toString());

    this._session.set(session);
  }

  /**
   * Session of the current user. Uses the one in memory or, after a reload, loads it from `GET /api/v1/session/me`
   * (one request while in flight). Emits `null` without session, without access to RT or without a valid
   * organization / store.
   */
  ensureSession(): Observable<AccountSummary | null> {
    const current = this.currentUser();
    if (current) {
      return of(current);
    }
    if (this.sessionLoad$) {
      return this.sessionLoad$;
    }

    this.sessionLoad$ = this.sessionApi.me().pipe(
      map((account) => this.fromSessionAccount(account)),
      tap((account) => this._session.set({ account })),
      catchError((error) => {
        console.error('Error loading the session:', error);
        return of(null);
      }),
      defaultIfEmpty(null),
      finalize(() => {
        this.sessionLoad$ = null;
      }),
      shareReplay(1),
    );
    return this.sessionLoad$;
  }

  private fromSessionAccount(account: SessionAccount): AccountSummary {
    if (!account.role) {
      throw new Error('NO_ROLE_ASSIGNED');
    }
    return this.withTenantAndStore({
      email: account.email,
      firstName: account.firstName ?? '',
      lastName: account.lastName ?? '',
      role: { name: account.role.name, description: account.role.description ?? '' },
      permissions: account.permissions ?? [],
      organizations: account.organizations ?? [],
    } as AccountSummary);
  }

  /** Tenant and store of the account: first organization (`tenantEquivalents`) and its first store. */
  private withTenantAndStore(account: AccountSummary): AccountSummary {
    const organization = account.organizations?.[0];
    if (!organization) {
      throw new Error('NO_ORGANIZATION_ASSIGNED');
    }

    const tenantEquivalent = tenantEquivalents[organization.organizationCode as TenantKey];
    if (!tenantEquivalent) {
      console.warn(`No tenant equivalent found for organizationCode: ${organization.organizationCode}`);
      throw new Error('INVALID_TENANT_MAPPING');
    }

    const store = organization.stores?.[0];
    if (!store) {
      throw new Error('NO_STORE_ASSIGNED');
    }

    return {
      ...account,
      tenant: {
        tenantId: tenantEquivalent.tenantId,
        tenantName: tenantEquivalent.tenantName,
      },
      store: {
        storeCode: store.storeCode,
        storeName: store.storeName,
      },
    };
  }

  /** Silent refresh: fresh Firebase ID token -> `POST /session/refresh`. Shared while in flight. */
  refreshSession(source: 'service' | 'interceptor', url = ''): Observable<void> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refresh$ = defer(() => from(this.firebase.auth.authStateReady())).pipe(
      switchMap(() => {
        const firebaseUser = this.firebase.auth.currentUser;
        if (!firebaseUser) {
          this.track('fail', 'No user found during silent refresh', { source, url });
          return EMPTY;
        }
        return from(getIdToken(firebaseUser, true));
      }),
      switchMap((freshToken: string) =>
        this.http.post(
          `${this.baseUrl}/session/refresh`,
          { firebaseToken: freshToken, productCode },
          { withCredentials: true },
        ),
      ),
      tap(() => localStorage.setItem(StorageKeys.AUTH_SESSION_ISSUED, Date.now().toString())),
      map(() => undefined as void),
    );

    this.refreshInProgress$ = refresh$.pipe(
      shareReplay(1),
      finalize(() => {
        this.refreshInProgress$ = null;
      }),
    );

    return this.refreshInProgress$;
  }

  /**
   * Password recovery. Legacy `changePasswordEmail` has no backend (returns null), so this
   * port keeps it unimplemented: it errors and the screen shows the "Lo sentimos" state.
   */
  changePasswordEmail(_email: string): Observable<{ status: 'success' | 'warning' | 'error' }> {
    return throwError(() => new Error('Password recovery is not implemented'));
  }

  /**
   * Evicts the cc-rt-core account cache first (the cookie is still valid; errors are ignored), then closes the
   * cc-auth session and Firebase.
   */
  logout(): Observable<void> {
    return this.sessionApi.evictCache().pipe(
      catchError(() => of(undefined)),
      switchMap(() => this.http.post(`${this.baseUrl}/session/logout`, {})),
      switchMap(() => from(signOut(this.firebase.auth))),
      catchError((error) => {
        console.error('Error logging out:', error);
        return from(signOut(this.firebase.auth));
      }),
      tap(() => {
        // `auth_user` is no longer written; removed to clean sessions stored by previous versions.
        localStorage.removeItem(StorageKeys.AUTH_USER);
        localStorage.removeItem(StorageKeys.AUTH_MODULE);
        localStorage.removeItem(StorageKeys.AUTH_SESSION_ISSUED);
        localStorage.removeItem(StorageKeys.MONITORING_TENANT);
        this._session.set(null);
      }),
    );
  }

  isUserAuthenticated(): boolean {
    return this._session() !== null;
  }

  getCurrentAuthResponse(): AuthResponse | null {
    return this._session();
  }

  getCurrentUser(): AccountSummary | null {
    return this.currentUser();
  }

  getCurrentUserModule(): Modules | null {
    return (localStorage.getItem(StorageKeys.AUTH_MODULE) as Modules | null) ?? null;
  }

  getPermissions(): string[] {
    return this.getCurrentUser()?.permissions ?? [];
  }

  hasPermissionFor(permissionId: string): boolean {
    const permissions = this.getCurrentUser()?.permissions;
    return !!permissions && permissions.some((p) => p === permissionId);
  }

  private track(result: EventTrackerResult, message: string, extra: Record<string, unknown> = {}): void {
    try {
      this.tracker.trackStep('auth_service', {
        result,
        payload: { message, ...extra },
      });
    } catch {
      /* ignored */
    }
  }
}
